import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { KeenIcon } from '@/components/keenicons';
import { useDebounce } from '@/hooks/use-debounce';
import { useUsers } from '@/hooks/admin/useUsers';

interface RoleUsersTabProps {
  roleId: string;
  roleName: string;
}

interface RoleUserItem {
  id: string;
  name?: string;
  userName?: string;
  email?: string;
  isActive?: boolean;
  creationTime?: string;
}

/**
 * Role Users Tab Component
 *
 * Displays users assigned to a specific role
 *
 * Features:
 * - List users with this role
 * - Search users
 * - Link to user details
 * - User statistics
 * - Pagination
 *
 * Note: This component uses the User service which should already exist
 * based on user.service.ts in the services directory
 *
 * Spec reference: Lines 336 and user management integration
 */
export function RoleUsersTab({ roleId, roleName }: RoleUsersTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Fetch users with this role
  const { data, isLoading, error } = useUsers({
    roleId: roleId,
    filter: debouncedSearch,
    maxResultCount: 50,
  });

  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Users with "{roleName}" Role</CardTitle>
              <CardDescription>
                View and manage users assigned to this role
              </CardDescription>
            </div>
            <Badge variant="secondary" className="w-fit text-sm">
              {data?.totalCount?.toLocaleString() || 0} user{data?.totalCount !== 1 ? 's' : ''}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="relative">
            <KeenIcon
              icon="magnifier"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="search"
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      {isLoading ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <KeenIcon icon="arrows-circle" className="mb-4 animate-spin text-4xl text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading users...</p>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <KeenIcon icon="information-circle" className="mb-4 text-4xl text-destructive" />
            <h3 className="mb-2 text-lg font-semibold">Failed to load users</h3>
            <p className="text-sm text-muted-foreground">
              {errorMessage}
            </p>
          </CardContent>
        </Card>
      ) : (data?.totalCount || 0) === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <KeenIcon icon="profile-circle" className="mb-4 text-4xl text-muted-foreground" />
            <h3 className="mb-2 text-lg font-semibold">No users found</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              {searchQuery
                ? 'No users match your search criteria'
                : `No users have been assigned the "${roleName}" role yet`}
            </p>
            <Button asChild variant="outline" className="gap-2">
              <Link to="/admin/users">
                <KeenIcon icon="profile-circle" />
                View All Users
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="hidden md:table-cell">Email</TableHead>
                    <TableHead className="hidden lg:table-cell">Status</TableHead>
                    <TableHead className="hidden lg:table-cell">Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.items?.map((user: RoleUserItem) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                            {user.name?.charAt(0) || user.userName?.charAt(0) || '?'}
                          </div>
                          <div>
                            <Link
                              to={`/users/${user.id}`}
                              className="font-semibold hover:text-primary hover:underline"
                            >
                              {user.name || user.userName}
                            </Link>
                            <p className="text-sm text-muted-foreground md:hidden">
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className="text-sm text-muted-foreground">{user.email}</span>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {user.isActive ? (
                          <Badge variant="outline" className="gap-1 border-green-600 text-green-600">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1 border-gray-400 text-gray-600">
                            <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <span className="text-sm text-muted-foreground">
                          {user.creationTime
                            ? new Date(user.creationTime).toLocaleDateString()
                            : '—'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="ghost" size="sm">
                          <Link to={`/users/${user.id}`}>
                            <KeenIcon icon="eye" />
                            <span className="sr-only">View user</span>
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm" className="gap-2">
            <Link to={`/users?roleId=${roleId}`}>
              <KeenIcon icon="filter" />
              Filter Users by Role
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm" className="gap-2">
            <Link to="/users/create">
              <KeenIcon icon="plus" />
              Add New User
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
